// REQUIREMENT INTAKE — the one door requirements come through.
//
// The whole dialog is two things: WHAT IT IS BASED ON, and WHAT YOU WANT.
// Everything else the AI decides. It used to ask for a story format up front —
// a decision nobody can make before reading the document it produces — and it
// had a button per file type, which pushed parsing into the browser and made
// every new format a frontend release. Both are gone: attach anything, say
// what you want, generate.
//
//   mode="new"    → collect sources + instructions, generate v1
//   mode="add"    → collect new material for an existing document, rebuild
//
// Files upload as bytes and nothing else. The backend classifies and reads
// them (documents parsed from S3, audio through the faster-whisper
// transcriber), so a source can be `parsing` or `transcribing` for a while —
// this polls until the words are in, because a document generated from a
// half-read source is worse than one that waited.
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ClipboardList, FileText, Loader2, Mic, MessagesSquare,
  Paperclip, Plus, Sparkles, Trash2, X, AppWindow,
} from "lucide-react";
import { fetchData } from "../../fetchapi/fetchapi";
import { listApps } from "../../services/apps";

const ICON_FOR = { audio: Mic, text: MessagesSquare, pdf: FileText, doc: FileText };

const reqApi = (path, body, method = "POST") =>
  fetchData(`requirements/${path}`, {
    method,
    ...(method === "POST" ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body || {}) } : {}),
  });

// Presigned PUT — the same handshake board attachments and chat uploads use,
// so source originals land inside the project's own prefix and nowhere else.
async function uploadOriginal(file, projectSlug) {
  const token = localStorage.getItem("access_token") || "";
  let signed;
  try {
    signed = await fetch("/api/chat/upload/sign", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "X-Cortex-Project": projectSlug,
        "X-Cortex-Branch": "main",
      },
      body: JSON.stringify({ filename: file.name, content_type: file.type || "application/octet-stream" }),
    });
  } catch {
    throw new Error("the app could not reach the server — check that it is running, then try again");
  }
  if (!signed.ok) {
    let msg = `HTTP ${signed.status}`;
    try { const j = await signed.json(); msg = j.message || j.error || msg; } catch { /* keep the status */ }
    throw new Error(msg);
  }
  const { url, key } = await signed.json();
  const put = await fetch(url, {
    method: "PUT",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!put.ok) throw new Error(`upload failed (HTTP ${put.status})`);
  return key;
}

const WAITING = new Set(["parsing", "transcribing"]);

function SourceRow({ src, onRemove }) {
  const Icon = ICON_FOR[src.kind] || FileText;
  const waiting = WAITING.has(src.status);
  const failed = src.status === "failed";
  return (
    <div className="flex items-center gap-2 text-xs rounded-md border border-page-divider px-2 py-1.5"
      data-testid={`req-src-${src.uiId}`}>
      <Icon size={12} className={`shrink-0 ${failed ? "text-red-600" : "text-page-muted"}`} />
      <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-page-text">{src.name}</span>
      {waiting ? (
        <span className="shrink-0 inline-flex items-center gap-1 text-page-muted">
          <Loader2 size={11} className="animate-spin" />
          {src.status === "parsing" ? "reading" : "transcribing"}{src.progress ? ` ${src.progress}%` : "…"}
        </span>
      ) : failed ? (
        <span className="shrink-0 text-red-600 max-w-[45%] truncate" title={src.error || ""}>
          {src.error || "failed"}
        </span>
      ) : (
        <span className="shrink-0 text-page-muted">{(src.chars || 0).toLocaleString()} chars</span>
      )}
      <button type="button" onClick={onRemove} title="Remove"
        className="bg-transparent border-0 cursor-pointer text-page-muted hover:text-red-600 p-0.5 shrink-0">
        <Trash2 size={12} />
      </button>
    </div>
  );
}

export default function RequirementIntake({
  mode = "new",          // "new" | "add"
  docSlug = null,        // required for "add"
  docTitle = null,
  projectSlug,
  onClose,
  onDone,                // (result) => void
}) {
  const isAdd = mode === "add";
  const [accept, setAccept] = useState("");
  const [supported, setSupported] = useState("");
  const [instructions, setInstructions] = useState("");
  const [appView, setAppView] = useState("");
  const [apps, setApps] = useState([]);
  const [sources, setSources] = useState([]);   // {uiId, id?, kind, name, text?, status, chars}
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteName, setPasteName] = useState("");
  const [pasteText, setPasteText] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState("");
  const [err, setErr] = useState("");
  const nextId = useRef(1);

  // What we can read is served, not hard-coded here: adding a format is a
  // backend change and this dialog picks it up with no release.
  useEffect(() => {
    let alive = true;
    reqApi("source_kinds", null, "GET")
      .then((r) => {
        if (!alive) return;
        setAccept(r?.accept || "");
        setSupported(r?.supported || "");
      })
      .catch(() => { if (alive) setSupported("documents, text and audio recordings"); });
    return () => { alive = false; };
  }, []);

  // Apps for the OPTIONAL link. A project with no apps simply has no picker.
  useEffect(() => {
    if (isAdd) return undefined;
    let alive = true;
    listApps({ projectSlug })
      .then((rows) => { if (alive) setApps(Array.isArray(rows) ? rows : rows?.apps || []); })
      .catch(() => { if (alive) setApps([]); });
    return () => { alive = false; };
  }, [projectSlug, isAdd]);

  // Poll whatever is still being read or transcribed. One timer for all of
  // them, stopped the moment nothing is pending.
  const pendingIds = sources.filter((s) => WAITING.has(s.status) && s.id).map((s) => s.id);
  const pendingKey = pendingIds.join(",");
  useEffect(() => {
    if (!pendingKey) return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const r = await reqApi(`sources?ids=${pendingKey}`, null, "GET");
        if (!alive) return;
        const byId = new Map((r?.sources || []).map((s) => [s.id, s]));
        setSources((list) => list.map((s) => {
          const fresh = s.id && byId.get(s.id);
          return fresh
            ? { ...s, kind: fresh.kind, status: fresh.status, progress: fresh.progress, chars: fresh.chars, error: fresh.error }
            : s;
        }));
      } catch { /* transient — the next tick retries */ }
    };
    const t = setInterval(tick, 3000);
    tick();
    return () => { alive = false; clearInterval(t); };
  }, [pendingKey]);

  // Refuse an unsupported file INSTANTLY, before uploading it. The backend
  // checks again — it is the authority — but a person who picked the wrong
  // file should not wait for a round trip to hear about it.
  const unsupported = useCallback((file) => {
    if (!accept) return null;   // registry not loaded — let the server decide
    const ext = (String(file.name).toLowerCase().match(/\.[a-z0-9]+$/) || [""])[0];
    const allowed = accept.split(",").map((x) => x.trim()).filter(Boolean);
    if (ext && allowed.includes(ext)) return null;
    return `"${file.name}" is not a format we can read. Supported: ${supported || allowed.join(", ")}.`;
  }, [accept, supported]);

  // Attach anything supported. The file is uploaded and registered; the
  // backend decides what it is and reads it. Nothing here parses.
  const addFiles = useCallback(async (e) => {
    // Snapshot BEFORE clearing the input — FileList is live in Chrome 148+.
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    setErr("");
    for (const f of files) {
      const why = unsupported(f);
      if (why) { setErr(why); continue; }
      const uiId = nextId.current++;
      try {
        setUploading(f.name);
        const key = await uploadOriginal(f, projectSlug);
        const r = await reqApi("sources", {
          ...(isAdd ? { doc_slug: docSlug } : {}),
          sources: [{ name: f.name, s3_key: key, content_type: f.type || null, bytes: f.size }],
        });
        const row = r?.sources?.[0];
        setSources((s) => [...s, {
          uiId, id: row?.id, kind: row?.kind, name: f.name,
          status: row?.status || "parsing", progress: 0, chars: row?.chars || 0,
        }]);
      } catch (ex) {
        // Name the FILE. "fetch failed" on its own left people staring at a
        // dialog with no idea which attachment, or which step, went wrong.
        setErr(`Could not attach "${f.name}": ${String(ex?.message || ex)}`);
      } finally {
        setUploading("");
      }
    }
  }, [projectSlug, isAdd, docSlug, unsupported]);

  // Dropping an attachment has to reach the server. In "add" mode the row is
  // registered against the document the moment it is attached (so a long
  // transcription outlives this dialog), and the rebuild reads EVERY ready row
  // for the document — so a purely local remove meant the file the user just
  // took out still shaped the next version. Retire it instead of deleting: the
  // record of what was uploaded is worth keeping, and the rebuild skips it.
  const removeSource = async (s, i) => {
    setSources((list) => list.filter((_, j) => j !== i));
    if (!s.id) return;
    try {
      await fetchData(`requirements/sources/${s.id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ excluded: true }),
      });
    } catch (e) {
      setErr(`Removed "${s.name}" here, but it could not be retired on the server (${String(e?.message || e)}) — retire it on the document before rebuilding.`);
    }
  };

  const addPaste = () => {
    const text = pasteText.trim();
    if (!text) return;
    const uiId = nextId.current++;
    setSources((s) => [...s, {
      uiId, kind: "text",
      name: pasteName.trim() || `pasted text ${s.length + 1}`,
      text, status: "ready", chars: text.length,
    }]);
    setPasteName(""); setPasteText(""); setPasteOpen(false);
  };

  const waiting = sources.some((s) => WAITING.has(s.status));
  const ready = sources.filter((s) => s.status === "ready");
  const canSubmit = !busy && !waiting &&
    (ready.length > 0 || instructions.trim().length > 0);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const inline = ready.filter((s) => !s.id).map((s) => ({ kind: s.kind || "text", name: s.name, text: s.text }));
      const ids = ready.filter((s) => s.id).map((s) => s.id);

      if (isAdd) {
        if (inline.length) await reqApi("sources", { doc_slug: docSlug, sources: inline });
        onDone(await reqApi(`${docSlug}/rebuild`, { source_ids: ids, instruction: instructions.trim() }));
        return;
      }
      // No format: the drafter chooses it from the sources and records its
      // reasoning on the document. This returns as soon as the work is
      // BOOKED — the document appears in the list when it is written, and a
      // failure appears there too, with its reason.
      onDone(await reqApi("draft", {
        brief: instructions.trim(), sources: inline, source_ids: ids,
        ...(appView ? { app_view: appView } : {}),
      }));
    } catch (e) {
      setErr(String(e?.message || (isAdd ? "rebuild failed" : "generation failed")));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="req-intake-dialog">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl border border-card-border bg-card-bg p-5 space-y-4">
        <div className="flex items-center gap-2">
          <ClipboardList size={18} className="text-nav-active" />
          <h2 className="text-lg font-medium text-page-title">
            {isAdd ? "Add to requirement" : "New requirement"}
          </h2>
          <button type="button" onClick={onClose} disabled={busy}
            className="ml-auto bg-transparent border-0 cursor-pointer text-page-muted hover:text-page-text p-1"><X size={16} /></button>
        </div>

        {/* SOURCES first: attaching the document is what people actually do first. */}
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-page-muted uppercase tracking-wide mr-auto">
              {isAdd ? "New material" : "Based on"}
            </span>
            <label data-testid="req-intake-attach"
              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30">
              <Paperclip size={12} /> Attach files
              <input type="file" multiple accept={accept || undefined} className="hidden" onChange={addFiles} />
            </label>
            <button type="button" onClick={() => setPasteOpen((o) => !o)} data-testid="req-intake-paste-toggle"
              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30">
              <Plus size={12} /> Paste text
            </button>
          </div>

          {uploading ? (
            <div className="text-xs text-page-muted inline-flex items-center gap-1.5">
              <Loader2 size={11} className="animate-spin" /> uploading {uploading}…
            </div>
          ) : null}

          {pasteOpen ? (
            <div className="rounded-md border border-page-divider p-2 space-y-2">
              <input value={pasteName} onChange={(e) => setPasteName(e.target.value)}
                placeholder='Where it came from, e.g. "Slack — Ivan, Aug 21"'
                data-testid="req-intake-paste-name"
                className="w-full text-sm px-2 py-1.5 rounded-md border border-page-divider bg-transparent text-page-text" />
              <textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} rows={5}
                placeholder="Paste the source text…" data-testid="req-intake-paste-text"
                className="w-full text-sm px-2 py-1.5 rounded-md border border-page-divider bg-transparent text-page-text font-mono" />
              <button type="button" onClick={addPaste} disabled={!pasteText.trim()} data-testid="req-intake-paste-add"
                className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30 disabled:opacity-50">
                <Plus size={12} /> Add
              </button>
            </div>
          ) : null}

          {sources.length ? sources.map((s, i) => (
            <SourceRow key={s.uiId} src={s} onRemove={() => removeSource(s, i)} />
          )) : (
            <div className="text-xs text-page-muted">
              {supported ? `Attach ${supported} — or paste text.` : "Attach a document or paste text."}
            </div>
          )}
        </div>

        {/* Then: what you want done with it. */}
        <label className="text-xs text-page-muted flex flex-col gap-1">
          {isAdd ? "What should change (optional when you attach new material)" : "What you want (optional when you attach a document)"}
          <textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={3}
            data-testid="req-intake-instructions"
            className="text-sm px-3 py-2 rounded-md border border-page-divider bg-transparent text-page-text"
            placeholder={isAdd
              ? "e.g. the escalation logic is wrong — high-risk vessels must also page the on-call officer"
              : "e.g. build the monthly compliance export from the attached spec, ignore the pricing section"} />
        </label>

        {!isAdd && apps.length > 0 && (
          <label className="text-xs text-page-muted flex flex-col gap-1">
            <span className="inline-flex items-center gap-1.5"><AppWindow size={12} /> App this is about (optional)</span>
            <select value={appView} onChange={(e) => setAppView(e.target.value)} data-testid="req-intake-app"
              className="text-sm px-2 py-2 rounded-md border border-page-divider bg-card-bg text-page-text">
              <option value="">— none —</option>
              {apps.map((a) => (
                <option key={a.id || a.view_name} value={a.view_name}>{a.title || a.name || a.view_name}</option>
              ))}
            </select>
          </label>
        )}

        {err ? <div className="text-sm text-red-600">{err}</div> : null}
        {waiting ? (
          <div className="text-xs text-page-muted">
            Reading what you attached — the document is written from the words, so it cannot start yet.
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} disabled={busy}
            className="text-sm px-3 py-2 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30 disabled:opacity-50">Cancel</button>
          <button type="button" onClick={submit} disabled={!canSubmit} data-testid="req-intake-submit"
            className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md border border-nav-active text-nav-active bg-nav-active/10 cursor-pointer hover:bg-nav-active/20 disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {busy ? (isAdd ? "Starting…" : "Starting…") : (isAdd ? "Update requirement" : "Create requirement")}
          </button>
        </div>
      </div>
    </div>
  );
}
